import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { Stack, Typography } from "@mui/material";

import DrmTable from "../tmt/DrmTable";
import { DRM_COLUMN } from "../../constData/columnDrmt";
import { DRMT_EXCEL_LABEL } from "../../constData/excelColumn";
import { ENUM_DATA } from "../../constData/enum";
import TableActionButtons from "../tmt/TableActionButtons";
import { getDrmChangeLogTableData } from "../../redux/reducers/drmChangeLogTableDataSlice";
import { setDrmTableFilter, resetDrmTableFilter } from "../../redux/reducers/drmTableFilterSlice";
import { setDrmTableFilter as setDrmDailyTableFilter, resetDrmTableFilter as resetDrmDailyTableFilter } from "../../redux/reducers/drmDailyTableFilterSlice";
import { useDrmColumnManagement } from "../../customHook/useDrmColumnManagement";
import { useDrmFilterUrlSync } from "../../customHook/useDrmFilterUrlSync";
import useExportChangeLogExcelData from "../../customHook/useExportChangeLogExcelData";
import { resetBidTableData, resetDailyTableData } from "../../redux/reducers/drmTableDataSlice";
import { drmtFilterPayload } from "../../constData/filterRequestPayloadDrmt";
import { drmtDailyFilterPayload } from "../../constData/filterRequestPayloadDrmtDaily";
import { pageSize, exportToExcel, stringify, mapRateToChargeFields, modecreation } from "../../helpers";


const INITIAL_TABLE_STATE = {
  originalData: [],
  headers: [],
  toggleProps: null,
  density: "small",
  currentPage: 1,
  updatedData: [],
  selectedRows: [],
  modifiedRows: {},
  newAddedRows: [],
};

const noopSetUiState = () => {};

function AuditHistoryDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const process = searchParams.get("process");

  const { drmChangeLogTableData, isLoading: isDrmChangeLogLoading } = useSelector((state) => state.drmChangeLog);
  const { tableFilter } = useSelector((state) => state.drmFilterPayload);
  const { defaultColumns, customViews } = useSelector((state) => state.customColumn);
  const { dailyTableFilter } = useSelector((state) => state.drmDailyFilterPayload);

  const isBid = process === "1";
  const tableType = isBid ? "drmBidTableData" : "drmDailyTableData";
  const selectedRows = useSelector((state) => state?.drmTableData?.[tableType]?.selectedRows || []);
  const defaultCols = isBid ? defaultColumns.bid : defaultColumns.daily;
  const customViewsData = isBid ? customViews.bid : customViews.daily;

  useDrmFilterUrlSync({
    scope: isBid ? "bid" : "daily",
    filterState: isBid ? tableFilter : dailyTableFilter,
    dispatch,
    setFilterAction: isBid ? setDrmTableFilter : setDrmDailyTableFilter,
    uiState: null,
    setUiState: noopSetUiState,
    initialUiState: null,
    defaultFilterState: isBid ? drmtFilterPayload : drmtDailyFilterPayload,
    countryOptions: [],
  });

  const { allColumnsWithStatus, toggleColumnVisibility, handleColumnReorder, orderedColumns: managedOrderedColumn } = useDrmColumnManagement(process, defaultCols, customViewsData);
  const { handleDownloadRecodes } = useExportChangeLogExcelData(process);
  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);
  const lastFetchedPageRef = useRef(null);
  const skipFilterEffectRef = useRef(false);
  const handleNextPageRef = useRef(null);

  const cloneFilters = useCallback((filters) => JSON.parse(JSON.stringify(filters ?? {})), []);

  const allowedKeys = useMemo(() => DRMT_EXCEL_LABEL.map((n) => ({ key: n.key, name: n.name })), []);

  const handleDownloadTableRecords = useCallback(() => {
    if (selectedRows.length > 0) {
      const formatted = selectedRows.map((item) => {
        const original = item?.original;
        const record = isBid
          ? { ...original, ...mapRateToChargeFields(original), mode: modecreation(original?.tariffId), qtyBucketType: original?.bucketType, bucketType: ENUM_DATA.Empty }
          : { ...original, ...mapRateToChargeFields(original), mode: modecreation(original?.tariffId) };
        return Object.fromEntries(allowedKeys.map(({ key, name }) => [name, stringify(record[key], key) || ""]));
      });
      exportToExcel(formatted, isBid ? "SelectedBidExcelExport" : "SelectedDailyExcelExport");
    } else {
      handleDownloadRecodes();
    }
  }, [selectedRows, isBid, allowedKeys, handleDownloadRecodes]);

  const buildFilters = useCallback(
    (useDefaults = false) => {
      if (process === "1") {
        const sourceFilters = useDefaults ? drmtFilterPayload.filters : tableFilter?.filters ?? drmtFilterPayload.filters;
        return {
          process,
          filters: cloneFilters(sourceFilters),
        };
      }

      const sourceFilters = useDefaults ? drmtDailyFilterPayload.filters : dailyTableFilter?.filters ?? drmtDailyFilterPayload.filters;

      return {
        process,
        filters: cloneFilters(sourceFilters),
      };
    },
    [cloneFilters, dailyTableFilter?.filters, process, tableFilter?.filters]
  );

  const filtersSignature = useMemo(() => {
    if (process === "1") {
      return JSON.stringify(tableFilter?.filters ?? drmtFilterPayload.filters);
    }
    return JSON.stringify(dailyTableFilter?.filters ?? drmtDailyFilterPayload.filters);
  }, [dailyTableFilter?.filters, process, tableFilter?.filters]);
  
  const prevFiltersSignatureRef = useRef(filtersSignature);

  const handleNextPage = useCallback(
    (page, options = {}) => {
      const filters = buildFilters(options.useDefaultFilters);
      const targetPage = options.resetToFirstPage ? 1 : page ?? 1;

      lastFetchedPageRef.current = targetPage;

      dispatch(
        getDrmChangeLogTableData({
          pageSize,
          pageNum: targetPage,
          tableName: "drmChangeLogTableData",
          reqBody: filters,
        })
      );
    },
    [buildFilters]
  );

  handleNextPageRef.current = handleNextPage;

  useEffect(() => {
    const previousSignature = prevFiltersSignatureRef.current;
    const hasFiltersChanged = previousSignature !== filtersSignature;
    prevFiltersSignatureRef.current = filtersSignature;

    if (!hasFiltersChanged) {
      return;
    }

    if (skipFilterEffectRef.current) {
      skipFilterEffectRef.current = false;
      if (tableState.currentPage !== 1) {
        setTableState((prev) => ({ ...prev, currentPage: 1 }));
      }
      return;
    }

    const targetPage = 1;
    if (tableState.currentPage !== targetPage) {
      setTableState((prev) => ({ ...prev, currentPage: targetPage }));
    }

    handleNextPageRef.current?.(targetPage, { resetToFirstPage: true });
  }, [filtersSignature, tableState.currentPage]);

  useEffect(() => {
    const targetPage = tableState.currentPage ?? 1;

    if (lastFetchedPageRef.current === targetPage) {
      return;
    }

    handleNextPageRef.current?.(targetPage);
  }, [tableState.currentPage]);

  const tableData = useMemo(() => drmChangeLogTableData?.drmMaintenanceDetails || [], [drmChangeLogTableData?.drmMaintenanceDetails]);

  const handleColumnOrder = useCallback(
    (action, columnName) => {
      handleColumnReorder(columnName, action);
    },
    [handleColumnReorder]
  );

  const handlePageRefresh = useCallback(
    (page, type) => {
      handleNextPage(page);
    },
    [handleNextPage]
  );

  const handleRefresh = useCallback(() => {
    if (isBid) {
      dispatch(resetDrmTableFilter());
      dispatch(resetBidTableData());
    } else {
      dispatch(resetDrmDailyTableFilter());
      dispatch(resetDailyTableData());
    }
    skipFilterEffectRef.current = true;
    setTableState(prev => ({ ...prev, currentPage: 1 }));
    handleNextPage(1, { useDefaultFilters: true, resetToFirstPage: true });
  }, [handleNextPage, isBid]);

  const columnOrder = useMemo(() => {
    const auditSpecificColumns = ["userId", "createdDate", "updatedDate"];

    const filteredColumnNames = managedOrderedColumn
      .filter((col) => col.isVisible)
      .map((col) => col.columnName)
      .filter((name) => name !== "bidFileName")
      .map((name) => (name === "lineHaulRate" ? "rate" : name))
      .filter((name, idx, arr) => arr.indexOf(name) === idx);

    const allColumns = [...filteredColumnNames, ...auditSpecificColumns];

    return allColumns
      .map((columnName) => DRM_COLUMN.find((col) => col.Header === columnName))
      .filter(Boolean);
  }, [managedOrderedColumn]);

  return (
    <Stack spacing={2} alignItems="flex-start" width="100%" px={2}>
      <Stack alignItems="flex-start">
        <Typography variant="h4" gutterBottom>
          {t("audit_history")}
        </Typography>
      </Stack>
      <TableActionButtons
        tableState={tableState}
        setTableState={setTableState}
        handleColumnOrder={handleColumnOrder}
        allColumnsWithStatus={allColumnsWithStatus}
        handleDownloadRecodes={handleDownloadTableRecords}
        onToggleColumnVisibility={toggleColumnVisibility}
      />
      <DrmTable
        tableName={isBid ? "Bid" : "Daily"}
        tableData={tableData}
        columnOrder={columnOrder}
        tableState={tableState}
        editableRow={false}
        allowRowSelection
        onPage={drmChangeLogTableData?.onPage}
        totalPages={drmChangeLogTableData?.totalPages}
        isLoadingDrmTable={isDrmChangeLogLoading}
        totalRecords={drmChangeLogTableData?.totalRecords}
        setTableState={setTableState}
        handleRefresh={handlePageRefresh}
        handleClear={handleRefresh}
        
      />
    </Stack>
  );
}

export default AuditHistoryDetails;
